import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import DurationUnitInput from './UI/DurationUnitInput';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import {
   listFreePromos,
   createFreePromo,
   stopFreePromo,
   type FreePromo,
   type CreateFreePromoBody,
   type RoutersReady,
} from '@/api/freePromo';

/**
 * Timed FREE-TIME PROMO on the operator Dashboard (Ray 2026-10-06, borrowed
 * from Kitifi/TarakiFi): "Free 30 min until 6pm" — every phone may claim it
 * ONCE. Lives on the Dashboard because it is a "do it now" marketing action
 * across vendos, not a setting of one vendo.
 *
 * Redesign (Ray 2026-10-23, from the approved HTML preview): an eye-catching
 * strip with one-tap starts when nothing runs; a LIVE card with the numbers
 * while one runs; a 3-step start window (Free time → Schedule → Vendos) with
 * tap choices instead of a calendar box; one day / every day / every week.
 */

/* ── time helpers ────────────────────────────────────────────────── */

const MINUTE_CHOICES = [15, 30, 60, 120];
/** Same ceiling the server enforces (PROMO_MAX_MINUTES). */
export const PROMO_MAX_MINUTES = 24 * 60;
const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Mon..Sun chip → JS weekday (0 = Sunday). */
const WEEK_DAY = [1, 2, 3, 4, 5, 6, 0];

type Win = { key: string; label: string; sub: string; start: number; end: number };
const WINDOWS: Win[] = [
   { key: 'lunch', label: 'Lunch', sub: '11 AM – 1 PM', start: 660, end: 780 },
   { key: 'merienda', label: 'Merienda', sub: '3 – 5 PM', start: 900, end: 1020 },
   { key: 'evening', label: 'Evening', sub: '6 – 9 PM', start: 1080, end: 1260 },
   { key: 'allday', label: 'All day', sub: 'whole day', start: 0, end: 1440 },
];
const RUN_CHOICES: { label: string; days: number | null }[] = [
   { label: '1 week', days: 7 },
   { label: '1 month', days: 30 },
   { label: '3 months', days: 90 },
   { label: 'Until I stop', days: null },
];

export function minutesLabel(m: number): string {
   const h = Math.floor(m / 60);
   const r = m % 60;
   if (!h) return `${r} min`;
   if (!r) return h === 1 ? '1 hour' : `${h} hours`;
   return `${h} hr ${r} min`;
}

/** 900 → "3:00 PM" */
export function clockLabel(min: number): string {
   if (min >= 1440) return '11:59 PM';
   const h24 = Math.floor(min / 60);
   const m = min % 60;
   const h = h24 % 12 || 12;
   return `${h}:${String(m).padStart(2, '0')} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

function minutesOfDay(d: Date): number {
   return d.getHours() * 60 + d.getMinutes();
}

/**
 * The "Now until …" end for a promo started right away: 6 PM, else 9 PM,
 * else the end of the day — whichever is still at least 30 minutes away.
 */
export function nowUntil(now = new Date()): number {
   const m = minutesOfDay(now);
   return [1080, 1260, 1440].find((e) => e - m >= 30) ?? 1440;
}

function atMinute(day: Date, min: number): Date {
   const d = new Date(day);
   if (min >= 1440) d.setHours(23, 59, 59, 0);
   else d.setHours(Math.floor(min / 60), min % 60, 0, 0);
   return d;
}

function dayLabel(d: Date): string {
   return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function daysText(days: number[]): string {
   const set = new Set(days);
   if (set.size === 7) return 'Every day';
   if (set.size === 2 && set.has(6) && set.has(0)) return 'Every Sat & Sun';
   return 'Every ' + WEEK.filter((_, i) => set.has(WEEK_DAY[i])).join(', ');
}

/** "Every Sat & Sun, 3:00 – 5:00 PM" / "Today, 3:00 – 5:00 PM" */
export function scheduleText(p: FreePromo, now = new Date()): string {
   if (p.repeat === 'daily' || p.repeat === 'weekly') {
      const when = p.repeat === 'daily' ? 'Every day' : daysText(p.days ?? []);
      const win =
         p.windowStart === 0 && p.windowEnd === 1440
            ? 'all day'
            : `${clockLabel(p.windowStart ?? 0)} – ${clockLabel(p.windowEnd ?? 1440)}`;
      return `${when}, ${win}`;
   }
   const s = new Date(p.startsAt);
   const e = new Date(p.endsAt);
   const today = new Date(now);
   today.setHours(0, 0, 0, 0);
   const sd = new Date(s);
   sd.setHours(0, 0, 0, 0);
   const day =
      sd.getTime() === today.getTime()
         ? 'Today'
         : sd.getTime() === today.getTime() + 86_400_000
           ? 'Tomorrow'
           : dayLabel(s);
   return `${day}, ${clockLabel(minutesOfDay(s))} – ${clockLabel(minutesOfDay(e))}`;
}

function leftLabel(untilIso: string | null | undefined, now = Date.now()): string {
   if (!untilIso) return '—';
   const ms = new Date(untilIso).getTime() - now;
   if (ms <= 0) return '0m';
   const m = Math.ceil(ms / 60_000);
   const h = Math.floor(m / 60);
   return h ? `${h}h ${m % 60}m` : `${m}m`;
}

/**
 * Routers still on an image that cannot draw the promo banner / announcement
 * on the WiFi page (OpenWrt before 2.6.33 / 2.7.10) are LOCKED in the vendo
 * pickers — greyed, untickable, tagged with the update they need (Ray
 * 2026-10-25, after Rhady set a promo on 5 Comfast EW71s and saw "0 phones").
 * MikroTik routers are never locked. Main id → version it needs.
 */
export const lockedMap = (r?: RoutersReady): Record<string, string> =>
   Object.fromEntries((r?.waitingList ?? []).map((w) => [w.id, w.version || '2.6.33']));

export const NeedsTag = styled.span`
   margin-left: auto;
   font-size: 11px;
   font-weight: 700;
   white-space: nowrap;
   color: #b45309;
   background: #fef3c7;
   border-radius: 999px;
   padding: 2px 8px;
`;

/* ── the panel ───────────────────────────────────────────────────── */

type Preset = Partial<Draft> & { step?: number };

const FreePromoPanel: React.FC = () => {
   const queryClient = useQueryClient();
   const [start, setStart] = useState<Preset | null>(null);
   const [confirmStop, setConfirmStop] = useState<FreePromo | null>(null);
   const [stopping, setStopping] = useState(false);
   const { data } = useQuery({
      queryKey: ['free-promos'],
      queryFn: listFreePromos,
      refetchInterval: 30_000,
   });
   const promos = data?.promos ?? [];
   const routers = data?.routers;
   const running = promos.filter((p) => p.status === 'live' || p.status === 'scheduled');
   const until = nowUntil();

   const doStop = async () => {
      if (!confirmStop || stopping) return;
      setStopping(true);
      try {
         await stopFreePromo(confirmStop._id);
         toast.success('Promo stopped');
         setConfirmStop(null);
         await queryClient.invalidateQueries({ queryKey: ['free-promos'] });
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Could not stop the promo');
      } finally {
         setStopping(false);
      }
   };

   return (
      <Wrap>
         {running.length === 0 && (
            <Invite data-testid="promo-invite">
               <Gift aria-hidden>🎁</Gift>
               <InviteText>
                  <Tag>BOOST YOUR SALES</Tag>
                  <InviteTitle>Give free WiFi, get new customers</InviteTitle>
                  <InviteSub>
                     A free taste brings people to your vendo, and they come back to pay. Start one in a few taps.
                  </InviteSub>
               </InviteText>
               <Quick>
                  <QuickBtn
                     type="button"
                     onClick={() => setStart({ minutes: 30, freq: 'once', day: 'today', win: 'now', step: 2 })}
                  >
                     Free 30 min<small>until {clockLabel(until).replace(':00', '')} today</small>
                  </QuickBtn>
                  <QuickBtn
                     type="button"
                     onClick={() =>
                        setStart({ minutes: 30, freq: 'weekly', weekDays: [6, 0], win: 'allday', run: 30, step: 2 })
                     }
                  >
                     Weekend promo<small>Sat & Sun</small>
                  </QuickBtn>
                  <QuickBtn type="button" $main onClick={() => setStart({})}>
                     + New promo<small>set it your way</small>
                  </QuickBtn>
               </Quick>
            </Invite>
         )}

         {running.map((p) => (
            <Live key={p._id} data-testid="promo-live" data-state={p.status} $state={p.status === 'live' ? 'live' : 'scheduled'}>
               <Gift aria-hidden>🎁</Gift>
               <InviteText>
                  <Pill $live={p.status === 'live'}>
                     {p.status === 'live' && <Blink />}
                     {p.status === 'live' ? 'LIVE NOW' : 'SCHEDULED'}
                  </Pill>
                  <LiveTitle>
                     Free {minutesLabel(p.minutes)}
                     {p.label ? ` · ${p.label}` : ''}
                  </LiveTitle>
                  <LiveSub>
                     {scheduleText(p)} · {p.deviceIds.length} vendo{p.deviceIds.length === 1 ? '' : 's'}
                     {p.repeat === 'daily' || p.repeat === 'weekly'
                        ? ` · ends ${new Date(p.endsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                        : ''}
                     {p.maxPhones != null ? ` · max ${p.maxPhones} per vendo` : ''}
                  </LiveSub>
               </InviteText>
               <Side>
                  <Stats>
                     <Stat>
                        <b>{p.claims}</b>
                        <span>phone{p.claims === 1 ? '' : 's'} got it</span>
                     </Stat>
                     <Stat>
                        <b>{p.status === 'live' ? leftLabel(p.liveUntil) : '—'}</b>
                        <span>{p.status === 'live' ? 'left today' : 'not on yet'}</span>
                     </Stat>
                     <Stat>
                        <b>₱{(p.paidAfter ?? 0).toLocaleString()}</b>
                        <span>paid after</span>
                     </Stat>
                  </Stats>
                  <Acts>
                     <SmallBtn type="button" onClick={() => setStart({})}>
                        + New
                     </SmallBtn>
                     <SmallBtn type="button" $danger onClick={() => setConfirmStop(p)}>
                        Stop promo
                     </SmallBtn>
                  </Acts>
               </Side>
            </Live>
         ))}

         {start && (
            <StartPromoModal
               preset={start}
               locked={lockedMap(routers)}
               onClose={() => setStart(null)}
               onStarted={async () => {
                  setStart(null);
                  await queryClient.invalidateQueries({ queryKey: ['free-promos'] });
               }}
            />
         )}
         {confirmStop &&
            createPortal(
               <Overlay>
                  <Dialog role="dialog" aria-label="Stop the promo">
                     <DlgBody>
                        <DialogTitle>Stop this promo now?</DialogTitle>
                        <Note>Phones that already got the free time keep it. New phones can no longer get it.</Note>
                     </DlgBody>
                     <Foot>
                        <FootBtn type="button" onClick={() => setConfirmStop(null)} disabled={stopping}>
                           Keep it running
                        </FootBtn>
                        <FootBtn type="button" $danger onClick={doStop} disabled={stopping}>
                           {stopping ? 'Stopping…' : 'Yes, stop it'}
                        </FootBtn>
                     </Foot>
                  </Dialog>
               </Overlay>,
               document.body
            )}
      </Wrap>
   );
};

export default FreePromoPanel;

/* ── the start window: 1 Free time → 2 Schedule → 3 Vendos ───────── */

interface Draft {
   minutes: number;
   maxPhones: string;
   label: string;
   freq: 'once' | 'daily' | 'weekly';
   day: 'today' | 'tomorrow' | 'pick';
   pickDate: string;
   weekDays: number[];
   win: string; // 'now' | WINDOWS key | 'custom'
   customStart: string;
   customEnd: string;
   run: number | null;
}

function toInputDate(d: Date): string {
   const p = (n: number) => String(n).padStart(2, '0');
   return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function hhmmToMin(v: string): number {
   const [h, m] = v.split(':').map(Number);
   return (h || 0) * 60 + (m || 0);
}

/** The request body for a draft, or a plain-English problem. */
export function buildPromoBody(
   d: Draft,
   deviceIds: string[],
   now = new Date()
): { body: CreateFreePromoBody } | { error: string; step: number } {
   // Any free time the operator types, not only the four quick picks
   // (Ray 2026-10-30, A59). The server takes 1 minute to 24 hours.
   if (!Number.isFinite(d.minutes) || d.minutes < 1)
      return { error: 'Type the free time for each phone, e.g. 45 minutes.', step: 0 };
   if (d.minutes > PROMO_MAX_MINUTES)
      return { error: 'Free time can be up to 24 hours per phone.', step: 0 };
   const max = d.maxPhones.trim() === '' ? null : Number(d.maxPhones);
   if (max !== null && (!Number.isInteger(max) || max < 1))
      return { error: 'Max phones must be a whole number, e.g. 50 — or leave it blank.', step: 0 };
   let start: number;
   let end: number;
   if (d.win === 'now') {
      start = -1;
      end = nowUntil(now);
   } else if (d.win === 'custom') {
      start = hhmmToMin(d.customStart);
      end = hhmmToMin(d.customEnd) || 1440;
      if (end - start < 15) return { error: 'The end time must be after the start, e.g. 9:00 AM to 12:00 PM.', step: 1 };
   } else {
      const w = WINDOWS.find((x) => x.key === d.win) ?? WINDOWS[1];
      start = w.start;
      end = w.end;
   }
   if (!deviceIds.length) return { error: 'Tick at least one vendo.', step: 2 };
   const common = { deviceIds, minutes: d.minutes, maxPhones: max, label: d.label.trim() };

   if (d.freq === 'once') {
      const day = new Date(now);
      if (d.day === 'tomorrow') day.setDate(day.getDate() + 1);
      if (d.day === 'pick') {
         const [y, m, dd] = d.pickDate.split('-').map(Number);
         if (!y) return { error: 'Pick the date of the promo.', step: 1 };
         day.setFullYear(y, m - 1, dd);
      }
      const startsAt = start < 0 ? now : atMinute(day, start);
      const endsAt = atMinute(day, end);
      if (endsAt.getTime() <= now.getTime() + 60_000)
         return { error: 'That time has already passed today — pick Tomorrow or another time.', step: 1 };
      return {
         body: { ...common, repeat: 'once', startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString() },
      };
   }
   if (start < 0) start = 0;
   if (d.freq === 'weekly' && !d.weekDays.length) return { error: 'Pick at least one day, e.g. Sat.', step: 1 };
   return {
      body: {
         ...common,
         repeat: d.freq,
         windowStart: start,
         windowEnd: end,
         ...(d.freq === 'weekly' ? { days: d.weekDays } : {}),
         runDays: d.run,
      },
   };
}

function summary(d: Draft, now = new Date()): string {
   let w: string;
   if (d.win === 'now') w = `now until ${clockLabel(nowUntil(now))}`;
   else if (d.win === 'custom') w = `${clockLabel(hhmmToMin(d.customStart))} – ${clockLabel(hhmmToMin(d.customEnd) || 1440)}`;
   else {
      const x = WINDOWS.find((y) => y.key === d.win)!;
      w = x.key === 'allday' ? 'all day' : `${clockLabel(x.start)} – ${clockLabel(x.end)}`;
   }
   const run = RUN_CHOICES.find((r) => r.days === d.run)?.label.toLowerCase() ?? '';
   const runText = d.run == null ? 'until you stop it' : `for ${run}`;
   let when: string;
   if (d.freq === 'once') {
      const day = new Date(now);
      if (d.day === 'tomorrow') day.setDate(day.getDate() + 1);
      when = d.day === 'today' ? 'Today' : d.day === 'tomorrow' ? 'Tomorrow' : d.pickDate ? dayLabel(new Date(`${d.pickDate}T00:00`)) : 'a day you pick';
      when += `, ${w}`;
   } else if (d.freq === 'daily') when = `every day, ${w}, ${runText}`;
   else {
      const days = WEEK.filter((_, i) => d.weekDays.includes(WEEK_DAY[i]));
      when = `every ${days.length ? days.join(' & ') : '— pick a day'}, ${w}, ${runText}`;
   }
   return `🎁 Free ${minutesLabel(d.minutes)} per phone — ${when}`;
}

export const StartPromoModal: React.FC<{
   preset?: Preset;
   /** main vendo id → router update it still needs; those rows are locked */
   locked?: Record<string, string>;
   onClose: () => void;
   onStarted: () => void;
}> = ({ preset = {}, locked = {}, onClose, onStarted }) => {
   const devices = useVendoDevicesStore((s) => s.devices);
   const fetched = useVendoDevicesStore((s) => s.fetched);
   const fetchDevices = useVendoDevicesStore((s) => s.fetch);
   useEffect(() => {
      if (!fetched) void fetchDevices();
   }, [fetched, fetchDevices]);

   // Mains, each followed by its sub-vendos. Sold vendos are left out.
   const rows = useMemo(() => {
      const own = devices.filter((d) => d.isCurrentOwner !== false);
      const mains = own.filter((d) => !d.parent).sort((a, b) => a.name.localeCompare(b.name));
      const out: { id: string; name: string; sub: boolean; locked?: string }[] = [];
      for (const m of mains) {
         out.push({ id: m._id, name: m.name, sub: false, locked: locked[m._id] });
         // a sub-vendo rides its main's router, so it is locked with it
         for (const s of own.filter((d) => d.parent === m._id))
            out.push({ id: s._id, name: s.name, sub: true, locked: locked[m._id] });
      }
      return out;
   }, [devices, locked]);
   const open = rows.filter((r) => !r.locked);

   const now = new Date();
   const [step, setStep] = useState(preset.step ?? 0);
   const [d, setD] = useState<Draft>({
      minutes: 30,
      maxPhones: '',
      label: '',
      freq: 'once',
      day: 'today',
      pickDate: toInputDate(new Date(now.getTime() + 2 * 86_400_000)),
      weekDays: [6, 0],
      win: 'now',
      customStart: '09:00',
      customEnd: '12:00',
      run: 30,
      ...preset,
   });
   const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
   // The typed-time box: open when the operator picks Custom, or when the
   // draft already holds a time that is not one of the quick picks.
   const [customTime, setCustomTime] = useState(() => !MINUTE_CHOICES.includes(d.minutes));
   const [picked, setPicked] = useState<Set<string> | null>(null);
   const [err, setErr] = useState('');
   const [busy, setBusy] = useState(false);
   // Every vendo ticked by default (Ray), once the list has loaded.
   const ticked = picked ?? new Set(open.map((r) => r.id));
   const toggle = (id: string) =>
      setPicked(() => {
         const next = new Set(ticked);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });

   // "Now" only makes sense for a promo that starts today.
   const nowOk = d.freq === 'once' && d.day === 'today';
   useEffect(() => {
      if (!nowOk && d.win === 'now') set('win', 'merienda');
   }, [nowOk, d.win]);

   const result = buildPromoBody(d, [...ticked], now);
   const next = () => {
      setErr('');
      if ('error' in result && result.step <= step) {
         setErr(result.error);
         return;
      }
      setStep((s) => Math.min(2, s + 1));
   };
   const submit = async () => {
      if (busy) return;
      if (!ticked.size) return setErr('Tick at least one vendo.');
      if ('error' in result) {
         setErr(result.error);
         setStep(result.step);
         return;
      }
      setErr('');
      setBusy(true);
      try {
         await createFreePromo(result.body);
         toast.success(d.freq === 'once' && d.win === 'now' ? 'Promo started' : 'Promo scheduled');
         onStarted();
      } catch (e: any) {
         setErr(e?.response?.data?.error || 'Could not start the promo');
      } finally {
         setBusy(false);
      }
   };

   const chip = (on: boolean) => ({ $on: on, type: 'button' as const });

   // Never closes on an outside click — only Cancel (Ray's modal rule).
   return createPortal(
      <Overlay>
         <Dialog role="dialog" aria-label="Start a free time promo">
            <Head>
               <TitleRow>
                  <DialogTitle>Start a free time promo</DialogTitle>
                  <CloseX type="button" aria-label="Close" onClick={onClose} disabled={busy}>
                     ✕
                  </CloseX>
               </TitleRow>
               <Steps aria-label={`Step ${step + 1} of 3`}>
                  {['Free time', 'Schedule', 'Vendos'].map((t, i) => (
                     <StepItem
                        key={t}
                        type="button"
                        $on={i === step}
                        $done={i < step}
                        aria-current={i === step ? 'step' : undefined}
                        onClick={() => i < step && setStep(i)}
                     >
                        <StepDot $on={i === step} $done={i < step}>
                           {i < step ? '✓' : i + 1}
                        </StepDot>
                        {t}
                     </StepItem>
                  ))}
               </Steps>
            </Head>

            <DlgBody>
               {step === 0 && (
                  <>
                     <div>
                        <Label>Free time for each phone</Label>
                        <Grid $n={5}>
                           {MINUTE_CHOICES.map((m) => (
                              <Chip
                                 key={m}
                                 {...chip(!customTime && d.minutes === m)}
                                 onClick={() => {
                                    setCustomTime(false);
                                    set('minutes', m);
                                 }}
                              >
                                 {minutesLabel(m)}
                              </Chip>
                           ))}
                           <Chip {...chip(customTime)} onClick={() => setCustomTime(true)}>
                              Custom
                           </Chip>
                        </Grid>
                        {customTime && (
                           <CustomTimeRow>
                              <DurationUnitInput
                                 value={d.minutes > 0 ? d.minutes : undefined}
                                 onChange={(v) => set('minutes', v ?? 0)}
                                 min={1}
                                 placeholder="Type"
                              />
                           </CustomTimeRow>
                        )}
                     </div>
                     <Grid $n={2}>
                        <div>
                           <Label htmlFor="fp-max">Max phones (optional)</Label>
                           <Input
                              id="fp-max"
                              type="text"
                              inputMode="numeric"
                              placeholder="No limit"
                              value={d.maxPhones}
                              onChange={(e) => set('maxPhones', e.target.value.replace(/[^0-9]/g, ''))}
                           />
                        </div>
                        <div>
                           <Label htmlFor="fp-label">Name (optional)</Label>
                           <Input
                              id="fp-label"
                              type="text"
                              maxLength={40}
                              placeholder="e.g. Merienda promo"
                              value={d.label}
                              onChange={(e) => set('label', e.target.value)}
                           />
                        </div>
                     </Grid>
                     <Note>
                        Each phone gets it once per day the promo runs. Phones that already have time keep their own
                        time. Some phones change their WiFi address and may get it twice — Max phones limits that.
                     </Note>
                  </>
               )}

               {step === 1 && (
                  <>
                     <div>
                     <Label>How often</Label>
                     <Seg>
                        {(
                           [
                              ['once', 'One day'],
                              ['daily', 'Every day'],
                              ['weekly', 'Every week'],
                           ] as const
                        ).map(([k, t]) => (
                           <SegBtn key={k} {...chip(d.freq === k)} onClick={() => set('freq', k)}>
                              {t}
                           </SegBtn>
                        ))}
                     </Seg>
                     </div>
                     {d.freq === 'once' && (
                        <div>
                           <Label>Which day</Label>
                           <Grid $n={3}>
                              <Chip {...chip(d.day === 'today')} onClick={() => set('day', 'today')}>
                                 Today<small>{dayLabel(now)}</small>
                              </Chip>
                              <Chip {...chip(d.day === 'tomorrow')} onClick={() => set('day', 'tomorrow')}>
                                 Tomorrow<small>{dayLabel(new Date(now.getTime() + 86_400_000))}</small>
                              </Chip>
                              <Chip {...chip(d.day === 'pick')} onClick={() => set('day', 'pick')}>
                                 Pick a date
                                 <small>{d.day === 'pick' ? dayLabel(new Date(`${d.pickDate}T00:00`)) : 'tap to choose'}</small>
                              </Chip>
                           </Grid>
                           {d.day === 'pick' && (
                              <Input
                                 aria-label="Promo date"
                                 type="date"
                                 style={{ marginTop: 6 }}
                                 min={toInputDate(now)}
                                 value={d.pickDate}
                                 onChange={(e) => set('pickDate', e.target.value)}
                              />
                           )}
                        </div>
                     )}
                     {d.freq === 'weekly' && (
                        <div>
                        <Label>Which days</Label>
                        <Grid $n={7}>
                           {WEEK.map((w, i) => {
                              const wd = WEEK_DAY[i];
                              const on = d.weekDays.includes(wd);
                              return (
                                 <Chip
                                    key={w}
                                    $noDot
                                    {...chip(on)}
                                    aria-pressed={on}
                                    onClick={() =>
                                       set('weekDays', on ? d.weekDays.filter((x) => x !== wd) : [...d.weekDays, wd])
                                    }
                                 >
                                    {w}
                                 </Chip>
                              );
                           })}
                        </Grid>
                        </div>
                     )}
                     <div>
                        <Label>Time</Label>
                        <Grid $n={3}>
                           <Chip {...chip(d.win === 'now')} disabled={!nowOk} onClick={() => set('win', 'now')}>
                              Now<small>until {clockLabel(nowUntil(now))}</small>
                           </Chip>
                           {WINDOWS.map((w) => (
                              <Chip key={w.key} {...chip(d.win === w.key)} onClick={() => set('win', w.key)}>
                                 {w.label}
                                 <small>{w.sub}</small>
                              </Chip>
                           ))}
                           <Chip {...chip(d.win === 'custom')} onClick={() => set('win', 'custom')}>
                              Custom<small>set hours</small>
                           </Chip>
                        </Grid>
                        {d.win === 'custom' && (
                           <Grid $n={2} style={{ marginTop: 6 }}>
                              <Input
                                 aria-label="Starts at"
                                 type="time"
                                 value={d.customStart}
                                 onChange={(e) => set('customStart', e.target.value)}
                              />
                              <Input
                                 aria-label="Ends at"
                                 type="time"
                                 value={d.customEnd}
                                 onChange={(e) => set('customEnd', e.target.value)}
                              />
                           </Grid>
                        )}
                     </div>
                     {d.freq !== 'once' && (
                        <div>
                           <Label>Keep running for</Label>
                           <Grid $n={4}>
                              {RUN_CHOICES.map((r) => (
                                 <Chip key={r.label} {...chip(d.run === r.days)} onClick={() => set('run', r.days)}>
                                    {r.label}
                                 </Chip>
                              ))}
                           </Grid>
                        </div>
                     )}
                  </>
               )}

               {step === 2 && (
                  <>
                     <div>
                        <Label>Vendos</Label>
                        <VendoList>
                           {open.length > 1 && (
                              <AllVendosRow
                                 total={open.length}
                                 ticked={open.filter((r) => ticked.has(r.id)).length}
                                 onChange={(all) => setPicked(new Set(all ? open.map((r) => r.id) : []))}
                              />
                           )}
                           {rows.map((r) => (
                              <VendoRow key={r.id} $sub={r.sub} $locked={!!r.locked} data-locked={r.locked || undefined}>
                                 <input
                                    type="checkbox"
                                    disabled={!!r.locked}
                                    checked={!r.locked && ticked.has(r.id)}
                                    onChange={() => toggle(r.id)}
                                 />
                                 {r.sub ? '↳ ' : ''}
                                 {r.name}
                                 {r.locked && !r.sub && <NeedsTag>needs router update {r.locked}</NeedsTag>}
                              </VendoRow>
                           ))}
                           {!rows.length && <Note>Loading your vendos…</Note>}
                           {rows.length > 0 && !open.length && (
                              <Note data-testid="all-locked">
                                 All your routers need router update {[...new Set(rows.map((r) => r.locked))].join(' / ')}{' '}
                                 first. Each one can run promos as soon as it updates.
                              </Note>
                           )}
                        </VendoList>
                     </div>
                     <Summary data-testid="promo-summary">{summary(d, now)}</Summary>
                  </>
               )}
               {err && <ErrText role="alert">{err}</ErrText>}
            </DlgBody>

            <Foot>
               <FootBtn type="button" $ghost onClick={step === 0 ? onClose : () => setStep(step - 1)} disabled={busy}>
                  {step === 0 ? 'Cancel' : 'Back'}
               </FootBtn>
               {step < 2 ? (
                  <FootBtn type="button" $primary onClick={next}>
                     Next
                  </FootBtn>
               ) : (
                  <FootBtn type="button" $primary onClick={submit} disabled={busy}>
                     {busy ? 'Starting…' : 'Start promo'}
                  </FootBtn>
               )}
            </Foot>
         </Dialog>
      </Overlay>,
      document.body
   );
};

/** "All vendos" tick box: ticks / unticks every row; part-ticked shows a dash. */
export const AllVendosRow: React.FC<{ total: number; ticked: number; onChange: (all: boolean) => void }> = ({
   total,
   ticked,
   onChange,
}) => {
   const ref = React.useRef<HTMLInputElement>(null);
   const all = total > 0 && ticked === total;
   useEffect(() => {
      if (ref.current) ref.current.indeterminate = ticked > 0 && ticked < total;
   }, [ticked, total]);
   return (
      <AllRow>
         <input ref={ref} type="checkbox" checked={all} onChange={() => onChange(!all)} aria-label="All vendos" />
         All vendos
         <AllCount>
            {ticked} of {total}
         </AllCount>
      </AllRow>
   );
};

/* ── styles ─────────────────────────────────────────────────────────── */

const AllRow = styled.label`
   position: sticky;
   top: 0;
   z-index: 1;
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 14px;
   font-weight: 700;
   padding: 10px 0;
   margin-bottom: 2px;
   border-bottom: 1px solid ${colors.GRAY13};
   background: ${colors.WHITE};
   color: var(--app-text);
   cursor: pointer;
`;
const AllCount = styled.span`
   margin-left: auto;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY3};
`;

const PINK = '#ff3d7f';
const bob = keyframes`
   0%, 100% { transform: translateY(0) rotate(-4deg); }
   50% { transform: translateY(-3px) rotate(4deg); }
`;
const pulse = keyframes`
   50% { opacity: 0.25; }
`;

const Wrap = styled.section`
   display: grid;
   gap: 10px;
   margin-bottom: 16px;
`;
const Invite = styled.div`
   position: relative;
   overflow: hidden;
   border-radius: 14px;
   padding: 10px 14px;
   color: #fff;
   background: linear-gradient(120deg, #ff6a3d 0%, ${PINK} 45%, #7b5cff 100%);
   display: grid;
   grid-template-columns: auto minmax(0, 1fr) auto;
   gap: 12px;
   align-items: center;
   box-shadow: 0 10px 24px rgb(255 61 127 / 0.25);
   &::before,
   &::after {
      content: '';
      position: absolute;
      border-radius: 50%;
      background: rgb(255 255 255 / 0.12);
      pointer-events: none;
   }
   &::before {
      width: 180px;
      height: 180px;
      right: -40px;
      top: -70px;
   }
   &::after {
      width: 110px;
      height: 110px;
      right: 170px;
      bottom: -60px;
   }
   @media (max-width: 820px) {
      grid-template-columns: auto minmax(0, 1fr);
   }
`;
const Gift = styled.div<{ $solid?: boolean }>`
   position: relative;
   z-index: 1;
   width: 42px;
   height: 42px;
   border-radius: 11px;
   display: grid;
   place-items: center;
   font-size: 23px;
   background: ${({ $solid }) => ($solid ? `linear-gradient(135deg, #ff6a3d, ${PINK})` : 'rgb(255 255 255 / 0.2)')};
   animation: ${bob} 2.4s ease-in-out infinite;
   @media (prefers-reduced-motion: reduce) {
      animation: none;
   }
`;
const InviteText = styled.div`
   position: relative;
   z-index: 1;
   min-width: 0;
`;
const Tag = styled.span`
   display: inline-block;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.06em;
   background: rgb(0 0 0 / 0.18);
   padding: 2px 7px;
   border-radius: 999px;
   margin-bottom: 3px;
`;
const InviteTitle = styled.div`
   font-size: 16px;
   font-weight: 800;
   line-height: 1.2;
`;
const InviteSub = styled.div`
   font-size: 12.5px;
   opacity: 0.92;
   margin-top: 1px;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;
const Quick = styled.div`
   position: relative;
   z-index: 1;
   display: grid;
   grid-template-columns: repeat(3, minmax(0, 1fr));
   gap: 8px;
   @media (max-width: 820px) {
      grid-column: 1 / -1;
   }
`;
const QuickBtn = styled.button<{ $main?: boolean }>`
   border: 1px solid ${({ $main }) => ($main ? '#fff' : 'rgb(255 255 255 / 0.45)')};
   background: ${({ $main }) => ($main ? '#fff' : 'rgb(255 255 255 / 0.14)')};
   color: ${({ $main }) => ($main ? PINK : '#fff')};
   border-radius: 9px;
   padding: 6px 12px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   line-height: 1.15;
   white-space: nowrap;
   small {
      display: block;
      font-weight: 500;
      font-size: 11px;
      opacity: 0.9;
   }
`;
// Live / scheduled promo: the same bright strip as the invite (Ray 2026-10-23:
// the plain white card "looks boring").
// Each state its own colour (Ray 2026-10-24: the running promo looked like the
// "start one" invitation): LIVE = green, SCHEDULED = blue, none = orange-pink.
const Live = styled(Invite)<{ $state: 'live' | 'scheduled' }>`
   padding: 9px 14px;
   background: ${({ $state }) =>
      $state === 'live'
         ? 'linear-gradient(120deg, #059669 0%, #10b981 50%, #14b8a6 100%)'
         : 'linear-gradient(120deg, #2563eb 0%, #4f46e5 55%, #7c3aed 100%)'};
   box-shadow: ${({ $state }) =>
      $state === 'live' ? '0 10px 24px rgb(16 185 129 / 0.28)' : '0 10px 24px rgb(79 70 229 / 0.25)'};
   @media (max-width: 900px) {
      grid-template-columns: auto minmax(0, 1fr);
   }
`;
const Pill = styled.span<{ $live: boolean }>`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.06em;
   padding: 2px 8px;
   border-radius: 999px;
   margin-bottom: 3px;
   color: ${({ $live }) => ($live ? '#e11d48' : '#7c3aed')};
   background: #fff;
`;
const Blink = styled.i`
   width: 7px;
   height: 7px;
   border-radius: 50%;
   background: #e11d48;
   animation: ${pulse} 1.2s infinite;
`;
const LiveTitle = styled.div`
   font-size: 16px;
   font-weight: 800;
   line-height: 1.2;
   color: #fff;
`;
const LiveSub = styled.div`
   font-size: 12.5px;
   color: rgb(255 255 255 / 0.92);
   margin-top: 1px;
`;
const Side = styled.div`
   position: relative;
   z-index: 1;
   display: grid;
   grid-template-columns: auto 200px;
   gap: 8px;
   align-items: stretch;
   @media (max-width: 900px) {
      grid-column: 1 / -1;
      grid-template-columns: minmax(0, 1fr);
   }
`;
const Stats = styled.div`
   display: grid;
   grid-template-columns: repeat(3, minmax(0, 1fr));
   gap: 8px;
`;
const Stat = styled.div`
   background: rgb(255 255 255 / 0.18);
   border: 1px solid rgb(255 255 255 / 0.3);
   border-radius: 9px;
   padding: 4px 10px;
   text-align: center;
   min-width: 78px;
   color: #fff;
   b {
      display: block;
      font-size: 15px;
      line-height: 1.15;
   }
   span {
      font-size: 11px;
      font-weight: 600;
      opacity: 0.9;
   }
`;
const Acts = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 6px;
`;
const SmallBtn = styled.button<{ $danger?: boolean }>`
   border-radius: 9px;
   padding: 6px 10px;
   font-size: 13px;
   font-weight: 700;
   white-space: nowrap;
   cursor: pointer;
   border: 1px solid ${({ $danger }) => ($danger ? '#fff' : 'rgb(255 255 255 / 0.55)')};
   background: ${({ $danger }) => ($danger ? '#fff' : 'rgb(255 255 255 / 0.14)')};
   color: ${({ $danger }) => ($danger ? '#e11d48' : '#fff')};
`;


const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgb(0 0 0 / 0.45);
   display: grid;
   place-items: center;
   padding: 16px;
   z-index: 1000;
   @media (max-width: 520px) {
      place-items: end center;
      padding: 0;
   }
`;
const Dialog = styled.div`
   background: ${colors.WHITE};
   color: var(--app-text);
   border-radius: 16px;
   width: 100%;
   max-width: 460px;
   max-height: 90vh;
   display: flex;
   flex-direction: column;
   @media (max-width: 520px) {
      border-radius: 16px 16px 0 0;
   }
`;
const Head = styled.div`
   padding: 20px 24px 14px;
   border-bottom: 1px solid ${colors.GRAY13};
   display: grid;
   gap: 10px;
`;
const DialogTitle = styled.h3`
   margin: 0;
   font-size: 16px;
   font-weight: 700;
`;
const TitleRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;
export const CloseX = styled.button`
   width: 32px;
   height: 32px;
   flex-shrink: 0;
   border: none;
   border-radius: 50%;
   background: ${colors.GRAY14};
   color: ${colors.GRAY3};
   font-size: 15px;
   line-height: 1;
   cursor: pointer;
   &:hover {
      color: var(--app-text);
   }
`;
const Steps = styled.div`
   display: grid;
   grid-template-columns: repeat(3, minmax(0, 1fr));
   gap: 6px;
   position: relative;
`;
const StepItem = styled.button<{ $on: boolean; $done: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   border: none;
   background: none;
   padding: 0;
   font-size: 13px;
   font-weight: ${({ $on }) => ($on ? 800 : 600)};
   color: ${({ $on, $done }) => ($on ? 'var(--app-text)' : $done ? colors.PRIMARY : colors.GRAY3)};
   cursor: ${({ $done }) => ($done ? 'pointer' : 'default')};
`;
const StepDot = styled.span<{ $on: boolean; $done: boolean }>`
   width: 24px;
   height: 24px;
   flex-shrink: 0;
   border-radius: 50%;
   display: grid;
   place-items: center;
   font-size: 12px;
   font-weight: 800;
   border: 2px solid ${({ $on, $done }) => ($on || $done ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on, $done }) => ($on ? colors.PRIMARY : $done ? 'var(--app-brand-tint)' : 'transparent')};
   color: ${({ $on, $done }) => ($on ? '#fff' : $done ? colors.PRIMARY : colors.GRAY3)};
`;
const DlgBody = styled.div`
   padding: 20px 24px;
   display: grid;
   gap: 18px;
   align-content: start;
   overflow: auto;
`;
const Foot = styled.div`
   padding: 16px 24px 20px;
   border-top: 1px solid ${colors.GRAY13};
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 8px;
`;
const FootBtn = styled.button<{ $primary?: boolean; $danger?: boolean; $ghost?: boolean }>`
   border-radius: 10px;
   padding: 10px;
   font-size: 14px;
   font-weight: 700;
   cursor: pointer;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: var(--app-text);
   ${({ $primary }) =>
      $primary &&
      css`
         background: ${colors.PRIMARY};
         border-color: ${colors.PRIMARY};
         color: #fff;
      `}
   ${({ $danger }) =>
      $danger &&
      css`
         background: #dc2626;
         border-color: #dc2626;
         color: #fff;
      `}
   ${({ $ghost }) =>
      $ghost &&
      css`
         background: transparent;
         border-color: transparent;
         color: ${colors.GRAY3};
         &:hover {
            color: var(--app-text);
         }
      `}
   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;
const Label = styled.label`
   display: block;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY3};
   margin-bottom: 6px;
   text-transform: uppercase;
   letter-spacing: 0.03em;
`;
const CustomTimeRow = styled.div`
   margin-top: 8px;
`;
const Grid = styled.div<{ $n: number }>`
   display: grid;
   grid-template-columns: repeat(${({ $n }) => $n}, minmax(0, 1fr));
   gap: 6px;
`;
// A choice: a card with a radio dot. Picked = solid blue edge + filled dot,
// so it never reads like a tab or an action button (Ray 2026-10-23).
export const Chip = styled.button<{ $on: boolean; $noDot?: boolean }>`
   position: relative;
   border: 2px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on }) => ($on ? 'var(--app-brand-tint)' : colors.WHITE)};
   color: ${({ $on }) => ($on ? colors.PRIMARY : 'var(--app-text)')};
   border-radius: 10px;
   padding: ${({ $noDot }) => ($noDot ? '9px 2px 8px' : '9px 4px 8px 22px')};
   font-size: 13px;
   font-weight: 700;
   text-align: center;
   cursor: pointer;
   line-height: 1.15;
   &::before {
      content: '';
      display: ${({ $noDot }) => ($noDot ? 'none' : 'block')};
      position: absolute;
      left: 8px;
      top: 50%;
      width: 10px;
      height: 10px;
      margin-top: -5px;
      border-radius: 50%;
      box-sizing: border-box;
      border: 2px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
      background: ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
      box-shadow: ${({ $on }) => ($on ? `inset 0 0 0 2px ${colors.WHITE}` : 'none')};
   }
   small {
      display: block;
      font-weight: 500;
      font-size: 11px;
      margin-top: 2px;
      color: ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY3)};
   }
   &:hover:not(:disabled) {
      border-color: ${colors.PRIMARY};
   }
   &:disabled {
      opacity: 0.35;
      cursor: default;
   }
`;
const Seg = styled.div`
   display: grid;
   grid-template-columns: repeat(3, minmax(0, 1fr));
   background: ${colors.GRAY14};
   border-radius: 10px;
   padding: 3px;
   gap: 3px;
`;
const SegBtn = styled.button<{ $on: boolean }>`
   border: none;
   border-radius: 8px;
   padding: 8px 4px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   background: ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
   color: ${({ $on }) => ($on ? '#fff' : 'var(--app-text)')};
   box-shadow: ${({ $on }) => ($on ? '0 2px 6px rgb(55 96 249 / 0.35)' : 'none')};
`;
const Input = styled.input`
   width: 100%;
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 9px;
   font-size: 14px;
   font-family: inherit;
`;
const VendoList = styled.div`
   max-height: 240px;
   overflow: auto;
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   padding: 4px 14px 6px;
   display: grid;
`;
const VendoRow = styled.label<{ $sub: boolean; $locked?: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 14px;
   padding: 8px 0 8px ${({ $sub }) => ($sub ? '22px' : '0')};
   color: ${({ $sub }) => ($sub ? colors.GRAY3 : 'var(--app-text)')};
   ${({ $locked }) => ($locked ? 'opacity: 0.55; cursor: not-allowed;' : '')}
`;
const Summary = styled.div`
   background: var(--app-brand-tint);
   color: ${colors.PRIMARY};
   border-radius: 10px;
   padding: 9px 11px;
   font-size: 13px;
   font-weight: 600;
`;
const Note = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
`;
const ErrText = styled.div`
   color: #dc2626;
   font-size: 13px;
`;
